import api from "@/axiosSetup";
import toast from "react-hot-toast";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { byPrefixAndName } from '@awesome.me/kit-a2910709fe/icons'
import { Model, Types } from "mongoose";
import { FileOrFolder } from "../../types/FileOrFolder";
import { IFile } from "../../types/IFile";
import dotenv from "dotenv";
dotenv.config();

export const BCR_ITEMS_PER_PAGE = 10;
export const MEETING_MINUTE_ITEMS_PER_PAGE = 10;
export const SUPPORTING_DOC_ITEMS_PER_PAGE = 15;

// File size limit
export const MAX_UPLOAD_MB = Number(process.env.NEXT_PUBLIC_FILE_SIZE_LIMIT) || 20; //20
export const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;

export const validateSizes = (list: File[]) => {
  const blockedFiles = (list ?? []).filter(shouldBlockUploadBySize);

  if (!blockedFiles.length) return true;

  toast.error(
    `Each non-image file must be ≤ ${MAX_UPLOAD_MB} MB.\n` +
      blockedFiles
        .map(f => `• ${f.name} (${formatBytes(f.size)})`)
        .join('\n') +
      `\n\nLarge images can be uploaded because the server will optimize them.`
  );

  return false;
};
// export const validateSizes = (list: File[]) => {
//   const oversize = (list ?? []).filter(f => f.size > MAX_UPLOAD_BYTES);
//   if (!oversize.length) return true;
//   toast.error(
//     `Each file must be ≤ ${MAX_UPLOAD_MB} MB.\n` +
//     oversize.map(f => `• ${f.name} (${formatBytes(f.size)})`).join('\n')
//   );
//   return false;
// };

export const PADDING = 10;
export const ITEM_WIDTH = 100 + PADDING;
export const ITEM_HEIGHT = 100 + PADDING;
// export const ITEM_WIDTH = 85 + PADDING;
// export const ITEM_HEIGHT = 85 + PADDING;
export const GRID_SIZE = ITEM_WIDTH;
export const MAX_COLUMNS = 9;
export const MAX_ROWS = 150; // 20, 150 rows = 1,350 BCRs!!! Viewed in File Explorer

export const userRoles = ["Admin", "Department Head", "Elected Official", "Records Manager"];
// export const userRoles = ["Department Head", "Band Administrator", "Council", "Chief"];
export const departments = ["Administration", "Education", "Fisheries", "Finance", "Governance", "Health", "Lands", "Housing", "Other"];
export const whereasOptions = ["Conditions", "Policy", "Legal", "Financial", "Community", "Environmental", "Other"];
export const fileTypes = ["bcr-pdf", "txt", "jpg"];
// export const itemAccessLevels = ['Default', 'Untouchable', 'Documentation', 'ImmutableShell', 'BCRs'];
export const itemAccessLevels = ['Default', 'Untouchable', 'Sheltered', 'ImmutableShell'];
// Default -> Any
// Untouchable -> Cannot be altered at all
// Sheltered -> Can be moved/altered but cannot leave folder directory
// ImmutableShell -> Cannot be renamed or moved, but properties can change
export const acceptedSignerRoles = ["Chief & Council", "Chief", "Council", "Band Admin/Councilor"];

export const folderColors = [
    "#ffa23e", "#ff4d4d", "#4d79ff", "#4dff4d", "#ffcc00",
    "#ff66b2", "#9900cc", "#33cccc", "#ccff66", "#000000"
];

const fileTypeIconMap: { [key: string]: keyof typeof byPrefixAndName.fas } = {
  pdf: "file-pdf",
  doc: "file-word",
  docx: "file-word",
  xls: "file-excel",
  xlsx: "file-excel",
  ppt: "file-powerpoint",
  pptx: "file-powerpoint",
  txt: "file-lines",
  csv: "file-csv",
  rtf: "file-lines",
  zip: "file-zipper",
  rar: "file-zipper",
  "7z": "file-zipper",
  tar: "file-zipper",
  gz: "file-zipper",
  xml: "file-code",
  json: "file-code",
  jpg: "file-jpg",
  jpeg: "file-jpg",
  png: "file-png",
  gif: "file-gif",
  mp4: "file-video",
  mov: "file-video",
  avi: "file-video",
  mp3: "file-audio",
  wav: "file-audio",
  bcr: "file-signature", // your custom type
};

export const getFileIcon = (type: string | undefined) => {
  if (!type) return byPrefixAndName.fas["file"];

  const iconName = fileTypeIconMap[type.toLowerCase()];
  return iconName ? byPrefixAndName.fas[iconName] : byPrefixAndName.fas["file"];
};

export type UploadSupportDocRef = {
  triggerFileUpload: () => void;
};

export const isTokenExpired = (token: string | null) => {
    if (!token) return true; // No token means expired or missing
  
    try {
      const payload = JSON.parse(atob(token.split(".")[1])); // Decode JWT payload
      return payload.exp * 1000 < Date.now(); // Compare expiry time
    } catch (error) {
      return true; // If decoding fails, consider the token expired
    }
};

export const formatDate = (date: string | Date) => {
    // ✅ Ensure `date` is a `Date` object
    const parsedDate = typeof date === "string" ? new Date(date) : date;

    return parsedDate.toLocaleString("en-US", {
        month: "long",  // February
        day: "2-digit", // 11
        year: "numeric", // 2026
        hour: "2-digit", // 1
        minute: "2-digit", // 32
        second: "2-digit", // 49
        hour12: true, // 12-hour format with AM/PM
    });
};
 
export const isAncestor = (itemId: string | null, targetParentId: string | null | undefined, allItems: FileOrFolder[]): boolean => {
    if (!targetParentId) return false;

    let currentParentId: string | null = targetParentId;

    while (currentParentId) {
        if (currentParentId === itemId) {
            return true; // Found circular dependency
        }

        const parentItem = allItems.find(item => item._id === currentParentId);
        currentParentId = parentItem ? parentItem.parentId ?? null : null; // Ensure null fallback
    }

    return false;
};

export const findAllChildren = (items: FileOrFolder[], parentId: string): (string | null)[] => {
    let childrenIds: (string | null)[] = [];

    function findChildren(id: string | null) {
        const children = items.filter(item => item.parentId === id);
        children.forEach(child => {
            childrenIds.push(child._id);
            findChildren(child._id); // Recursive lookup
        });
    }

    findChildren(parentId);
    return childrenIds;
};

export const notificationVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 }
};

export const checkSpaceBeforeCreate = async (parentId: string | null, numItems: number) => {
    const client = getApiClient();
    const res = await client.post("/api/files-folders/check-available-grid-space", {
      parentId,
      itemsToCreate: numItems,
    });
  
    if (!res.data.ok) {
      toast.error(res.data.message || "Not enough space to create folder.");
      return false;
    }
    return true;
};

export const truncateWithSmartDots = (text: string, amount: number=24) => {
    const base = text.slice(0, amount); // always show the first 24 chars
    const dotCount = Math.min(text.length - amount, 3); // up to 3 dots
    const dots = dotCount > 0 ? '.'.repeat(dotCount) : '';
    return base + dots;
};

export const isDescendant = (itemId: string, targetId: string, allItems: FileOrFolder[]): boolean => {
    const lookup = new Map(allItems.map(i => [i._id, i.parentId]));

    let current = lookup.get(targetId);
    while (current) {
        if (current === itemId) return true;
        current = lookup.get(current);
    }
    return false;
};


export function filterValidMoveItems({
    items,
    newParentId,
    allItems,
  }: {
    items: FileOrFolder[];
    newParentId: string | null;
    allItems: FileOrFolder[];
  }) {
    const validItems: FileOrFolder[] = [];
    const invalidItems: FileOrFolder[] = [];
  
    for (const item of items) {
      if (!item._id) continue;
  
      const isInvalidMove =
        newParentId === item._id ||
        isDescendant(item._id, newParentId!, allItems);
  
      if (isInvalidMove) {
        invalidItems.push(item);
      } else {
        validItems.push(item);
      }
    }
  
    return { validItems, invalidItems };
}

export function isIFile(doc: unknown): doc is IFile {
  return typeof doc === 'object' && doc !== null && 'url' in doc;
}
export function isFileOrFolder(doc: unknown): doc is FileOrFolder {
  return typeof doc === 'object' && doc !== null && 'url' in doc;
}

// OLD?
// export function countKeywordMatches(content: string, keywords: string[]): number {
//   if (!content || keywords.length === 0) return 0;

//   // const normalize = (text: string) =>
//   //   text.toLowerCase().trim().replace(/[^\w\s]/g, '').replace(/\s+/g, ' ');

//   const normalize = (text: string) =>
//   text
//     .toLowerCase()
//     .trim()
//     .replace(/[^\w\s]/g, ' ') // replace punctuation with a space
//     .replace(/\s+/g, ' ');

//   const normalizedContent = normalize(content);
//   let totalMatches = 0;

//   for (const keyword of keywords) {
//     const normalizedKeyword = normalize(keyword);
//     if (!normalizedKeyword) continue;

//     const regex = new RegExp(`(^|\\s)${escapeRegExp(normalizedKeyword)}(?=\\s|$)`, 'gi');
//     const matches = normalizedContent.match(regex);
//     if (matches) {
//       totalMatches += matches.length;
//     }
//   }

//   return totalMatches;
// }
// NEW?
export function countKeywordMatches(content: string, keywords: string[]): number {
  if (!content || keywords.length === 0) return 0;

  const normalize = (s: string) =>
    s.toLowerCase()
      // turn any non letter/number into a single space
      .replace(/[^a-z0-9]+/gi, ' ')
      .trim();

  const normalizedContent = normalize(content);

  let total = 0;
  for (const raw of keywords) {
    const norm = normalize(raw);
    if (!norm) continue;

    // Build a pattern that allows *any* non-alnum (or none) between tokens.
    // e.g. "thank you" -> /(^|\W)thank\W*you(?=\W|$)/gi
    const tokens = norm.split(' ').filter(Boolean).map(escapeRegExp);
    const pattern = `(^|\\W)${tokens.join('\\W*')}(?=\\W|$)`;
    const re = new RegExp(pattern, 'gi');

    const matches = normalizedContent.match(re);
    if (matches) total += matches.length;
  }
  return total;
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// // INCLUDES BREAK POINTS
// export function hasKeywordMatch(item: FileOrFolder, keywords: string[]): boolean {
//   if (!item || keywords.length === 0) return false;

//   const normalizeText = (text: string) =>
//     text.replace(/\s+/g, ' ').toLowerCase().trim();

//   const valuesToSearch = [
//     item.name,
//     item.type,
//     item.content,
//     ...(item.metadata || []),
//   ].map((val) => normalizeText(val || ''));

//   return keywords.some((keyword) => {
//     const normKeyword = keyword.toLowerCase().trim();
//     const regex = new RegExp(`\\b${escapeRegExp(normKeyword)}\\b`, 'i'); // full-word match
//     return valuesToSearch.some((val) => regex.test(val));
//   });
// }

export function hasKeywordMatch(item: FileOrFolder, keywords: string[]): boolean {
  if (!item || keywords.length === 0) return false;

  // const normalize = (text: string) =>
  //   text.toLowerCase().trim().replace(/[^\w\s]/g, '').replace(/\s+/g, ' ');

  const normalize = (text: string) =>
  text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s]/g, ' ') // replace punctuation with a space
    .replace(/\s+/g, ' ');

  const valuesToSearch = [
    item.name,
    item.type,
    item.content,
    ...(item.metadata || []),
  ].map((val) => normalize(val || ''));

  return keywords.some((keyword) => {
    const normKeyword = normalize(keyword);
    const regex = new RegExp(`(^|\\s)${escapeRegExp(normKeyword)}(?=\\s|$)`, 'i');
    return valuesToSearch.some((val) => regex.test(val));
  });
}

export function hasKeywordMatch_NoMetadata(item: FileOrFolder, keywords: string[]): boolean {
  if (!item || keywords.length === 0) return false;

  const normalize = (text: string) =>
    text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s]/g, " ") // replace punctuation with spaces
      .replace(/\s+/g, " ");

  // ✅ Only search these fields (NOT metadata)
  const valuesToSearch = [item.name, item.type, item.content].map((val) =>
    normalize(val || "")
  );

  return keywords.some((keyword) => {
    const normKeyword = normalize(keyword);
    if (!normKeyword) return false;

    const regex = new RegExp(
      `(^|\\s)${escapeRegExp(normKeyword)}(?=\\s|$)`,
      "i"
    );

    return valuesToSearch.some((val) => regex.test(val));
  });
}


export const convertToLegalText = async (rawText: string): Promise<string | null> => {
  try {
    const res = await api.post(
      '/api/gpt/refine-bcr-language',
      { rawText },
      {
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: true, // 🔑 This ensures cookies/sessions are sent
        timeout: 35_000,
      }
    );

    return res.data?.convertedText ?? null;
  } catch {
    return null;
  }
};

export const newObjectId = () =>
Array.from(crypto.getRandomValues(new Uint8Array(12)))
  .map(b => b.toString(16).padStart(2, "0"))
  .join("");

// helper (optional): nice file size display
export const formatBytes = (bytes: number) => {
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0, n = bytes;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
};

export const isSafari = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  // covers iOS & macOS Safari (not Chrome/Firefox on iOS)
  return /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS/.test(ua);
};

export async function ensureSessionFresh(): Promise<boolean> {
  try {
    const client = getApiClient();
    // Will 200 if access is good, or refresh and then 200.
    // If refresh fails, server returns 401 with a reason and this throws.
    await client.get("/api/users/check", { withCredentials: true });
    // await api.get("/api/users/check", { withCredentials: true });
    return true;
  } catch (e) {
    // Your interceptor may already handle logout/toast on 401 refresh failure.
    return false;
  }
}

export const stripHtml = (html: string) => html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();

import DOMPurify, { Config } from "dompurify";
import { getApiClient } from "./getApiClient";

// const purifyConfig: Config = {
//   ALLOWED_TAGS: ["b","i","u","strong","em","span","mark","br","ul","ol","li","p","a"],
//   ALLOWED_ATTR: ["href","target","rel","style"],
//   ADD_ATTR: ["rel"],
//   FORBID_ATTR: ["onerror","onclick"],
//   ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
// };

// DOMPurify.addHook("afterSanitizeAttributes", (node) => {
//   if (!(node instanceof HTMLElement)) return;

//   // lock down <a>
//   if (node.tagName === "A") {
//     const href = node.getAttribute("href") || "";
//     if (href) {
//       node.setAttribute("rel", "noopener noreferrer");
//       node.setAttribute("target", "_blank");
//     }
//   }

//   // lock down style: allow ONLY text-transform + font-size
//   if (node.hasAttribute("style")) {
//     const style = node.getAttribute("style") || "";

//     const allowed: string[] = [];

//     const tt = style.match(/text-transform\s*:\s*([a-z-]+)\s*;?/i);
//     if (tt && ["uppercase","lowercase","capitalize"].includes(tt[1].toLowerCase())) {
//       allowed.push(`text-transform: ${tt[1].toLowerCase()}`);
//     }

//     const fs = style.match(/font-size\s*:\s*([0-9.]+)(px|rem|em|%)\s*;?/i);
//     if (fs) {
//       allowed.push(`font-size: ${fs[1]}${fs[2]}`);
//     }

//     if (allowed.length) node.setAttribute("style", allowed.join("; ") + ";");
//     else node.removeAttribute("style");
//   }
// });

// export function SafeHTML({ html, className }: { html: string; className?: string }) {
//   const clean = DOMPurify.sanitize(html, purifyConfig);
//   return <div className={className} dangerouslySetInnerHTML={{ __html: clean }} />;
// }

// Put this helper somewhere shared (utils/date.ts)
export function dateOnlyInZoneAsDate(timeZone: string, base = new Date()) {
  // 1) Get YYYY-MM-DD for "today" in the reserve timezone
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(base);

  const get = (t: string) => parts.find(p => p.type === t)?.value ?? "";
  const y = Number(get("year"));
  const m = Number(get("month"));
  const d = Number(get("day"));

  // 2) Return a stable Date representing that day (use noon UTC to avoid DST bugs)
  return new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
}

export function toReserveDateOnly(value: Date, timeZone: string) {
  // get the calendar day as seen in the reserve timezone
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(value);

  const get = (t: string) => parts.find(p => p.type === t)?.value ?? "";
  const y = Number(get("year"));
  const m = Number(get("month"));
  const d = Number(get("day"));

  // noon UTC avoids DST “previous/next day” shifts
  return new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
}

export function normalizeEmptyParagraphs(html: string) {
  return html
    // <p></p> or <p>   </p>
    .replace(/<p>\s*<\/p>/gi, '<p data-empty="1"></p>')
    // <p><br></p> / <p><br/></p>
    .replace(/<p>\s*<br\s*\/?>\s*<\/p>/gi, '<p data-empty="1"></p>')
    // <p>&nbsp;</p>
    .replace(/<p>\s*&nbsp;\s*<\/p>/gi, '<p data-empty="1"></p>');
}

export const normalizeRteHtml = (html?: string) => {
  if (!html) return "";

  let out = html;

  // Normalize NBSP
  out = out.replace(/\u00A0/g, " ").replace(/&nbsp;/g, " ");

  // Empty paragraphs are authored blank lines in both BCR editor modes.
  // Preserve them, including at the start/end; trim only outside the HTML.
  return out.trim();
};

export function ensureBcrSectionSpacing(html: string) {
  if (!html) return html;

  let out = html;

  // 1) Normalize empty paragraphs to a real visual blank line
  out = out.replace(/<p>\s*<\/p>/gi, "<p><br></p>");

  // 2) If a block ends and the next block is a "heading paragraph",
  // insert a blank line between them.
  //
  // Heading paragraph heuristic:
  // <p><strong>Something:</strong></p>
  // or <p><strong><span ...>Something:</span></strong></p>
  const headingPara =
    `<p>\\s*<strong>(?:\\s*<span[^>]*>)?[^<]+(?:<\\/span>)?\\s*<\\/strong>\\s*<\\/p>`;

  const rx = new RegExp(`(<\\/p>|<\\/ul>|<\\/ol>)\\s*(${headingPara})`, "gi");
  out = out.replace(rx, `$1<p><br></p>$2`);

  // 3) Collapse excessive blank lines (max 2 in a row)
  out = out.replace(/(?:<p><br><\/p>\s*){3,}/gi, "<p><br></p><p><br></p>");

  return out.trim();
}

export const getUserDisplayName = (createdUser: any): string | undefined => {
  if (typeof createdUser === "object" && createdUser !== null) {
    return `${createdUser.firstName} ${createdUser.lastName}`;
  }
  return undefined;
};

export const intendedRecipientSuggestions = [
  "Government (Federal)",
  "Government (Provincial)",
  "Government (Municipal)",
  "Government (First Nation)",
  "Auditors",
  "Other",
];

export const canServerOptimizeUpload = (file: File) => {
  const ext = file.name.split(".").pop()?.toLowerCase();

  return (
    file.type.startsWith("image/") ||
    ["bmp", "webp", "avif", "heic", "heif", "jpg", "jpeg", "png", "tif", "tiff"].includes(ext || "")
  );
};

export const shouldBlockUploadBySize = (file: File) => {
  return file.size > MAX_UPLOAD_BYTES && !canServerOptimizeUpload(file);
};

// Used in google drive picker
export const canServerOptimizeByNameOrMime = (name: string, mimeType?: string | null) => {
  const ext = name.split(".").pop()?.toLowerCase();

  return (
    mimeType?.startsWith("image/") ||
    ["bmp", "webp", "avif", "heic", "heif", "jpg", "jpeg", "png", "tif", "tiff"].includes(ext || "")
  );
};

export const capitalizeFirst = (value?: string | null) => {
  if (!value) return "N/A";

  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};
